import { RoleType } from './../constants/role'
import { MediaType } from './../constants/types'
import { ReviewStatus } from '@/constants/metadata'
import { AssetType } from '@/constants/types'
import { ProjectRole } from '@/constants/role'
import { Store } from 'vuex'
import { State } from '@/store/state'
import { ComputedRef } from 'vue'

export type EventComposition = {
  bind: () => void
  unbind: () => void
}

export type ApiRespone<T> = {
  code: string
  statusCode: number
  message?: string
  result?: T
  size?: number
  count?: number
  page?: number
}

export type BatchResponse<T> = {
  success: T[]
  error: {
    message: string
    code: string
  }
}

export enum FileStatus {
  'Queued',
  'Uploading',
  'Paused',
  'Complete',
  'Failed',
  'Import_Queued',
  'Importing',
  'Import_Complete',
  'Import_Failed',
}

export type SortType = 'create_date' | 'name' | 'media.typenum' | 'audit'

export type UIState = {
  selected?: boolean
  state?: FileStatus
  percentage?: number
  speed?: string
}

export type Project = {
  name: string
  id: string
  root_asset: string
  owner_id: string
  owner: Owner
  role: ProjectRole
  assigned_space: number
  is_readonly: boolean
  is_private: boolean
  is_top: boolean
  order: number
  team: string
  permission?: Permission
} & UIState

export type Permission = {
  editor_can_download: boolean
  editor_can_share: boolean
  editor_can_collection: boolean
  reviewer_can_download: boolean
  reviewer_can_share: boolean
  reviewer_can_collection: boolean
}
export type DeleteUser = {
  user_code: string
} & AvatarLike

export type AvatarLike = {
  nick_name: string
  avatar_url: string
}

export type Asset = {
  name: string
  id: string
  description: string
  owner: Owner
  owner_id: string
  delete_user?: DeleteUser
  delete_time?: string
  type: AssetType
  version: string
  create_date: string
  project: Project
  project_id: string
  parent_asset_id: string
  audit: ReviewStatus
  labels: Array<Label>
  child_count: number
  space: number
  media: Media
} & UIState

export type NeedUploadAsset = Asset & {
  upload_urls: string[]
  upload_id: string
}

export type Media = {
  media_type: MediaType
  size: number
  proxy_path: string
  thumbnail_path: string
  slide_thumbnail_path: string
  orignal_path: string
  fps: number
  duration: number
  frames: number
  audio_count: number
  audio_sampling_rate: number
  bit_rate: number
  codec: string
  scan_type: string
  width: number
  height: number
}

export type Label = {
  id: string
  name: string
}

export interface Audit {
  guid: string;
  displayName: string;
  color: string;
  key: string;
  order: number;
  type: string
  disable: boolean
}

export type Owner = {
  user_code: string
} & AvatarLike

export type User = {
  email: string
  phone: string
  id: number
  role: RoleType
  user_code: string
  login_name: string
  psw_state: number
  teams: Array<Team>
} & AvatarLike

export type Collaborator = {
  email: string
  phone: string
  id: number
  role: ProjectRole
  user_code: string
  login_name: string
} & AvatarLike

export type Team = {
  id: number
  description: string
  code: string
  name: string
  type: string
}

export enum MenuType {
  SEPARATOR,
  RENAME,
  DELETE,
  MOVE,
  COPY,
  DOWNLOAD,
  CREATE,
  REFRESH,
  UPLOAD_FILE,
  UPLOAD_FOLDER,
  SHARE,
  RESTORE,
  PURGE,
  PURGE_ALL,
  REVIEW_STATUS,
  PUBLISH,
  CREATE_NOTE,
  PICTURE_IMAGINE,
  SPEECH_TO_TEXT
}

export type Menu = {
  id: MenuType
  label: ComputedRef<string>
  left?: string
  right?: string
  data?: any
  description?: string
  show?: (store: Store<State>, data?: Array<Asset>) => Boolean
  callback?: (menu: Menu, data: any, store: Store<State>) => void
  checkable?: boolean
  submenus?: Menu[]
  checked?: boolean
  disabled?: boolean
}

export type Menuable = (store: Store<State>, files?: Asset[]) => Menu

export type MenuOrMenuable = Menu | Menuable

export type MenuComputer = (menus: MenuOrMenuable[]) => MenuOrMenuable[]

export type Dropdown = {
  action: string
  label: ComputedRef<string>
  left?: string
  show?: (store: Store<State>, folder?: Asset | null) => Boolean
}

export type KeyEvent = {
  ctrlKey?: boolean
  shiftKey?: boolean
  metaKey?: boolean
  key: string
  action?: string
  callback?: Function
  test?: (...args: any) => Boolean
}

export type Comment = {
  id: string
  annotation: string
  aspect_ratio: string
  asset_id: string
  asset: Asset
  parent_id?: string
  isPrivate: string
  frame: number
  has_replies: number
  date: string
  owner: Owner
  owner_id: string
  text: string
  thumb: string
  type: string
  duration: number
}

export type Field = {
  ctrl: string
  name?: string
  value?: string
  key: string
  multiple?: boolean
  onChange?: Function
  onItemClick?: Function
  placeholder?: string
  options?: Array<{
    label: string
    value: string
  }>
}

export type Condition = {
  id: string
  key: string
  op: string
  value: string
}

export type SharePreference = {
  enable_comment: Boolean
  enable_upload?: Boolean
  enable_download: Boolean
}
export type FTPPreference = {
  enable_upload: Boolean
  enable_download: Boolean
}
export type Collection = {
  name: string
  usercode: string | number
  avatar_url: string
  username: string
  teamcode: string
  code: string
  description: string
  asset_id: string
  verify: string
  is_valid: Boolean
  enable_verify: Boolean
  power: SharePreference
  expiration_time: Date | string
  effective_time: Date | string
}

export type Share = {
  name: string
  avatar_url: string
  username: string
  count: number
  usercode: string | number
  teamcode: string
  code: string
  asset_ids: string
  verify: string
  enable_verify: Boolean
  is_valid: Boolean
  power: SharePreference
  expiration_time: Date | string
  effective_time: Date | string
}

export type FTP = {
  id: number
  name: string
  avatar_url: string
  username: string
  usercode: string | number
  teamcode: string
  code: string
  asset_ids: string
  verify: string
  enable_verify: Boolean
  is_valid: Boolean
  power: FTPPreference
  expiration_time: Date | string
  effective_time: Date | string
}
export type Notification<T> = {
  message: string
  id: string
  type: string
  time: string
  read: boolean
  source_user: AvatarLike
  extend: T
}
